import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { getDisplayRuntimeProfile } from 'config/displayRuntime';
const proximalColor = getComputedStyle(document.documentElement).getPropertyValue('--text-section-proximal').trim();
const midColor = getComputedStyle(document.documentElement).getPropertyValue('--text-section-mid').trim();
const distalColor = getComputedStyle(document.documentElement).getPropertyValue('--text-section-distal').trim();

export interface DataPoint {
  x: number;
  y: number;
}
export interface SelectedIndices {
  proximal: number;
  mid: number;
  distal: number;
}

interface LineChartProps {
  data?: DataPoint[] | null;
  x: (d: DataPoint) => number;
  y: (d: DataPoint) => number;
  yDomain: number[];
  width: number;
  height: number;
  yLabel: string;
  title?: string;
  color: string;
  strokeWidth: number;

  selectedIndices?: SelectedIndices;
  dragNodeId: number;

  draggingKey: 'proximal' | 'mid' | 'distal' | null;

  onMarkerMouseDown?: (key: 'proximal' | 'mid' | 'distal' | null) => void;
  onDragStart?: (e: PointerEvent, index: number) => void;
  onDrag?: (e: PointerEvent, index: number) => void;
  onDragEnd?: () => void;

  windowSize?: number;
}

function movingAverage(arr: ArrayLike<number>, window: number) {
  if (window <= 1) return Array.from(arr);
  const out = [];
  const n = arr.length;
  const half = Math.floor(window / 2);
  for (let i = 0; i < n; ++i) {
    let sum = 0,
      cnt = 0;
    for (let j = i - half; j <= i + half; ++j)
      if (j >= 0 && j < n) {
        sum += arr[j];
        cnt++;
      }
    out.push(sum / cnt);
  }
  return out;
}

const LineChart = ({
  data,
  x,
  y,
  yDomain,
  width,
  height,
  yLabel,
  title,
  color,
  strokeWidth,
  selectedIndices,
  dragNodeId,
  draggingKey,
  onMarkerMouseDown,
  onDragStart,
  onDrag,
  onDragEnd,
  windowSize = 10,
}: LineChartProps): React.JSX.Element => {
  const [chartData, setChartData] = useState<DataPoint[]>(data ?? []);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const gRef = useRef<SVGGElement | null>(null);
  const [, setDragging] = useState(false);
  const draggingRef = useRef(false);
  const lastDragIndexRef = useRef<number | null>(null);
  const callbacksRef = useRef({
    onMarkerMouseDown,
    onDragStart,
    onDrag,
    onDragEnd,
  });

  useEffect(() => {
    callbacksRef.current = {
      onMarkerMouseDown,
      onDragStart,
      onDrag,
      onDragEnd,
    };
  });

  const fontScale = useMemo(() => {
    if (!width || !height) return 1;
    const wScale = width / 720;
    const hScale = height / 280;
    const typographyScale = getDisplayRuntimeProfile().typographyScale;
    return Math.max(0.7, Math.min(1.22, Math.min(wScale, hScale) * typographyScale));
  }, [width, height]);
  const axisFontSize = Math.max(9, Math.round(12 * fontScale));
  const titleFontSize = Math.max(11, Math.round(13 * fontScale));
  const labelFontSize = Math.max(10, Math.round(12 * fontScale));
  const margin = useMemo(
    () => ({
      top: Math.max(14, Math.round(labelFontSize * 1.6)),
      right: Math.max(30, Math.round(axisFontSize * 3.5)),
      bottom: Math.max(
        40,
        Math.round((axisFontSize + labelFontSize) * 2.2),
      ),
      left: Math.max(46, Math.round(labelFontSize * 3.8)),
    }),
    [axisFontSize, labelFontSize],
  );
  const innerWidth = useMemo(
    () => Math.max(0, width - margin.left - margin.right),
    [width, margin.left, margin.right],
  );
  const innerHeight = useMemo(
    () => Math.max(0, height - margin.top - margin.bottom),
    [height, margin.top, margin.bottom],
  );
  const xExtent = useMemo(
    () =>
      chartData.length ? (d3.extent(chartData, x) as [number, number]) : [0, 0],
    [chartData, x],
  );
  const xScale = useMemo(
    () => d3.scaleLinear().domain(xExtent).range([0, innerWidth]),
    [xExtent, innerWidth],
  );
  const yScale = useMemo(
    () => d3.scaleLinear().domain(yDomain).range([innerHeight, 0]),
    [yDomain, innerHeight],
  );
  const xAxis = useMemo(
    () =>
      d3
        .axisBottom(xScale)
        .ticks(Math.max(2, Math.floor(width / 120)))
        .tickSizeOuter(0),
    [xScale, width],
  );
  const yAxis = useMemo(
    () => d3.axisLeft(yScale).ticks(Math.floor(innerHeight / 30)),
    [yScale, innerHeight],
  );

  useEffect(() => {
    if (!data) return;
    const yArr = data.map((d) => d.y);
    const smoothY = movingAverage(yArr, windowSize);
    setChartData(data.map((d, i) => ({ x: d.x, y: smoothY[i] })));
  }, [data, windowSize]);

  const clear = (which: 'all' | 'three' | 'one') => {
    const svg = d3.select(svgRef.current);
    if (which === 'all') {
      svg.selectAll('*').remove();
      svg
        .attr('width', width)
        .attr('height', height)
        .attr('viewBox', `0 0 ${width} ${height}`)
        .style('background', '#181818');
    } else if (which === 'three') svg.selectAll('.marker-group').remove();
    else svg.selectAll('.one-line').remove();
  };

  const draw = () => {
    const bottomSafe = Math.max(4, Math.round(labelFontSize * 0.4));

    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr(
        'style',
        'max-width:100%; height:auto; height:intrinsic; background-color:#0D0D0D;',
      );
    svg.selectAll('*').remove();

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    gRef.current = g.node();

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(xAxis)
      .selectAll('text')
      .attr('fill', 'white')
      .attr('font-size', `${axisFontSize}px`);
    g.append('g')
      .call(yAxis)
      .call((gg) =>
        gg
          .selectAll('text')
          .attr('fill', '#fff')
          .attr('font-size', `${axisFontSize}px`),
      );
    g.append('line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', 'white')
      .attr('stroke-width', 1);
    g.append('line')
      .attr('x1', innerWidth)
      .attr('x2', innerWidth)
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', 'white')
      .attr('stroke-width', 1);

    const path = d3
      .line<DataPoint>()
      .x((d) => xScale(d.x))
      .y((d) => yScale(d.y));
    g.append('path')
      .datum(chartData)
      .attr('fill', 'none')
      .attr('stroke', 'white')
      .attr('stroke-width', strokeWidth)
      .attr('d', path);

    if (title)
      d3.select(svgRef.current)
        .append('text')
        .attr('x', width / 2)
        .attr('y', Math.max(12, Math.round(14 * fontScale)))
        .attr('text-anchor', 'middle')
        .attr('fill', '#FFE066')
        .attr('font-size', `${titleFontSize}px`)
        .text(title);
    d3.select(svgRef.current)
      .append('text')
      .attr('x', width / 2)
      .attr('y', height - bottomSafe)
      .attr('dominant-baseline', 'text-after-edge')
      .attr('text-anchor', 'middle')
      .attr('fill', 'white')
      .attr('font-size', `${labelFontSize}px`)
      .text('Length (mm)');
    d3.select(svgRef.current)
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -height / 2)
      .attr('y', Math.max(10, Math.round(18 * fontScale)))
      .attr('fill', '#fff')
      .attr('text-anchor', 'middle')
      .attr('font-size', `${labelFontSize}px`)
      .text(yLabel);

    const getNearestDataIndex = (targetX: number) => {
      const lastIndex = chartData.length - 1;
      if (lastIndex < 0) return -1;
      if (lastIndex === 0) return 0;

      const firstX = chartData[0].x;
      const lastX = chartData[lastIndex].x;
      if (!Number.isFinite(firstX) || !Number.isFinite(lastX)) {
        let nearest = 0;
        let nearestDistance = Number.POSITIVE_INFINITY;
        for (let i = 0; i <= lastIndex; i += 1) {
          const distance = Math.abs(chartData[i].x - targetX);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearest = i;
          }
        }
        return nearest;
      }

      const ascending = firstX <= lastX;
      if (ascending) {
        if (targetX <= firstX) return 0;
        if (targetX >= lastX) return lastIndex;
      } else {
        if (targetX >= firstX) return 0;
        if (targetX <= lastX) return lastIndex;
      }

      let lo = 0;
      let hi = lastIndex;
      while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        const midX = chartData[mid].x;
        if ((ascending && midX < targetX) || (!ascending && midX > targetX)) {
          lo = mid + 1;
        } else {
          hi = mid;
        }
      }

      const prev = Math.max(0, lo - 1);
      return Math.abs(chartData[prev].x - targetX) <=
        Math.abs(chartData[lo].x - targetX)
        ? prev
        : lo;
    };

    // 차트 별 마우스 event 컨트롤
    svg.on('mousedown', (event: any) => {
      event.preventDefault();
      draggingRef.current = true;
      setDragging(true);
      callbacksRef.current.onMarkerMouseDown?.(null);
      const [mouseX] = d3.pointer(event, g.node());
      const idx = getNearestDataIndex(xScale.invert(mouseX));
      if (idx >= 0) {
        lastDragIndexRef.current = idx;
        callbacksRef.current.onDragStart?.(event, idx);
      }
    });
    svg.on('mousemove', (event: any) => {
      if (!draggingRef.current) return;
      event.preventDefault();
      const [mouseX] = d3.pointer(event, g.node());
      const idx = getNearestDataIndex(xScale.invert(mouseX));
      if (idx >= 0 && idx !== lastDragIndexRef.current) {
        lastDragIndexRef.current = idx;
        callbacksRef.current.onDrag?.(event, idx);
      }
    });
    svg.on('mouseup', () => {
      draggingRef.current = false;
      lastDragIndexRef.current = null;
      setDragging(false);
      callbacksRef.current.onDragEnd?.();
    });
    // svg.on('mouseleave', () => {
    //   setDragging(false);
    //   onDragEnd?.();
    // });
    // ~ 차트 별 마우스 event 컨트롤

    return () => {
      svg.on('mousedown', null);
      svg.on('mousemove', null);
      svg.on('mouseup', null);
      // svg.on('mouseleave', null);
    };
  };

  const drawMarkers = () => {
    if (!selectedIndices || !chartData.length) return;

    const g = d3.select(gRef.current);
    g.selectAll('.marker-group').remove();

    const strokeWidth = 2;
    const markers: {
      key: 'proximal' | 'mid' | 'distal';
      idx: number;
      color: string;
    }[] = [
      { key: 'proximal', idx: selectedIndices.proximal, color: proximalColor },
      { key: 'mid', idx: selectedIndices.mid, color: midColor},
      { key: 'distal', idx: selectedIndices.distal, color: distalColor},
    ];

    const y0 = yDomain[0],
      y1 = yDomain[1];
    const xScale = d3
      .scaleLinear()
      .domain(d3.extent(chartData, (d) => d.x) as [number, number])
      .range([0, innerWidth]);

    const yScale = d3.scaleLinear().domain([y0, y1]).range([innerHeight, 0]);
    markers.forEach(({ key, idx, color }) => {
      if (idx !== -1 && chartData[idx]) {
        const xPos = xScale(chartData[idx].x);

        const markerGroup = g.append('g')
        .attr('class','marker-group')
        .attr('data-key',key)

        // 히트영역
        markerGroup.append('line')
          .attr('class', 'three-line-hit')
          .attr('x1', xPos)
          .attr('x2', xPos)
          .attr('y1', yScale(y0))
          .attr('y2', yScale(y1))
          .attr('stroke', color)
          .attr('stroke-width', strokeWidth + 10)
          .attr('stroke-opacity', 0)
          .style('cursor', 'move')
          .on('mousedown', (ev: any) => {
            ev.preventDefault();
            ev.stopPropagation();
            draggingRef.current = true;
            lastDragIndexRef.current = idx;
            setDragging(true);
            callbacksRef.current.onMarkerMouseDown?.(key);
            callbacksRef.current.onDragStart?.(ev, idx);
          });

        markerGroup.append('line')
          .attr('class', 'three-line')
          .attr('x1', xPos)
          .attr('x2', xPos)
          .attr('y1', yScale(y0))
          .attr('y2', yScale(y1))
          .attr('stroke', color)
          .attr('stroke-width', strokeWidth)
          .style('pointer-events', 'none');
      }
    });
  };

  const drawActiveLine = () => {
    clear('one');
    if (dragNodeId === -1 || !chartData[dragNodeId] || !gRef.current) return;

    const gSel = d3.select(gRef.current);
    const d = chartData[dragNodeId];
    const xPos = xScale(d.x);
    const textY = innerHeight / 5;

    if (draggingKey === null) {
      gSel
        .append('line')
        .attr('class', 'one-line')
        .attr('x1', xPos)
        .attr('x2', xPos)
        .attr('y1', 0)
        .attr('y2', innerHeight)
        .attr('stroke', '#33FF33')
        .attr('stroke-width', 2);
    }
    gSel
      .append('text')
      .attr('class', 'one-line')
      .attr('x', xPos - 6)
      .attr('y', textY)
      .attr('text-anchor', 'end')
      .attr('fill', 'white')
      .attr('font-size', `${Math.max(10, Math.round(14 * fontScale))}px`)
      .attr('font-weight', 'bold')
      .text(() => `${Number.isInteger(d.y) ? d.y : d.y.toFixed(0)} HU`);
  };

  useEffect(() => {
    clear('all');
    draw();
    drawMarkers();
  }, [
    width,
    height,
    chartData,
    yDomain,
    innerWidth,
    innerHeight,
    margin.left,
    margin.top,
    xAxis,
    yAxis,
    xScale,
    yScale,
    title,
    yLabel,
    fontScale,
    axisFontSize,
    titleFontSize,
    labelFontSize,
    strokeWidth,
  ]);
  useEffect(() => {
    clear('three');
    drawMarkers();
  }, [selectedIndices, yDomain]);
  useEffect(() => {
    drawActiveLine();
  }, [dragNodeId, draggingKey, chartData, xScale, innerHeight, fontScale]);

  return <svg ref={svgRef} style={{ userSelect: 'none' }} />;
};
export default React.memo(LineChart);
